"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { Stats } from "@react-three/drei";
import * as THREE from "three";
import EarthSphere, { EarthSphereRef } from "./earth/earth-sphere";
import EarthClouds from "./earth/earth-clouds";
import EarthAtmosphere from "./earth/earth-atmosphere";
import SatelliteSystem, { SatelliteSystemRef } from "./satellites/satellite-system";
import CameraControls from "./camera/camera-controls";
import CameraAnimator, { CameraAnimatorRef } from "./camera/camera-animator";
import GlobeAccessibilityControls from "./globe-accessibility-controls";
import GlobeTextualAlternativeModal from "./globe-textual-alternative-modal";
import { INITIAL_CAMERA_POSITION } from "../constants";
import { useFormatter, useTranslations } from "next-intl";
THREE.ColorManagement.enabled = true;

// Constantes del componente original
const EARTH_ROTATION_SPEED = 2 * Math.PI / (24 * 60 * 60 * 1000);
const VIEW_OFFSET_RATIO = 0;
const EARTH_X_OFFSET = -15;
const params = {
  sunIntensity: 1.5,
  speedFactor: 1,
  metalness: 0.1,
  atmOpacity: {
    value: 0.2
  },
  atmPowFactor: {
    value: 4.1
  },
  atmMultiplier: {
    value: 9.5
  }
};
export interface SatelliteData {
  id: string;
  name: string;
  lastImportedAt: string;
  originalPosition: {
    latitude: number;
    longitude: number;
  };
  currentPosition: {
    latitude: number;
    longitude: number;
  };
  altitude: number;
  speed: number;
  heading: number;
}
export interface EarthGlobeProps {
  satellites?: SatelliteData[];
  style?: React.CSSProperties;
  showStats?: boolean;
  selectedSatelliteIndex?: number | null;
  earthXOffset?: number;
  legendContent?: React.ReactNode;
}

// Tipo exportado para las posiciones en tiempo real de los satélites
export interface SatellitePosition {
  latitude: number;
  longitude: number;
}

// Vectores reutilizables para el cálculo de posiciones fuera del render loop
const _tmpWorldPos = new THREE.Vector3();
const _tmpRelative = new THREE.Vector3();

/**
 * Calcula las posiciones en tiempo real (lat/lon) de todos los satélites
 * a partir de las mallas 3D y la rotación actual de la Tierra.
 */
function computeSatellitePositions(satelliteSystemRef: React.RefObject<SatelliteSystemRef>, earthSphereRef: React.RefObject<EarthSphereRef>, earthXOffset: number): SatellitePosition[] {
  const count = satelliteSystemRef.current?.getCount() ?? 0;
  const earthRotation = earthSphereRef.current?.getEarthRotation() ?? 0;
  const positions: SatellitePosition[] = [];
  for (let i = 0; i < count; i++) {
    const mesh = satelliteSystemRef.current?.getSatelliteMesh(i);
    if (!mesh) {
      positions.push({
        latitude: 0,
        longitude: 0
      });
      continue;
    }
    mesh.getWorldPosition(_tmpWorldPos);
    _tmpRelative.set(_tmpWorldPos.x - earthXOffset, _tmpWorldPos.y, _tmpWorldPos.z);
    const distance = _tmpRelative.length();
    const latitude = Math.asin(_tmpRelative.y / distance) * (180 / Math.PI);
    const worldLonAngle = Math.atan2(_tmpRelative.z, _tmpRelative.x);
    const longitudeRad = -(worldLonAngle + earthRotation);
    let longitude = longitudeRad * 180 / Math.PI;
    while (longitude > 180) longitude -= 360;
    while (longitude < -180) longitude += 360;
    positions.push({
      latitude,
      longitude
    });
  }
  return positions;
}

// Componente de configuración de cámara - maneja el offset de vista
function CameraSetup() {
  const $ = _c(4);
  const {
    camera,
    size
  } = useThree();
  let t0;
  let t1;
  if ($[0] !== camera || $[1] !== size) {
    t0 = () => {
      if (camera instanceof THREE.PerspectiveCamera) {
        const viewOffsetX = size.width * VIEW_OFFSET_RATIO;
        camera.setViewOffset(size.width + viewOffsetX * 2, size.height, viewOffsetX, 0, size.width, size.height);
      }
    };
    t1 = [camera, size];
    $[0] = camera;
    $[1] = size;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  React.useEffect(t0, t1);
  return null;
}

// Componente de luz solar con declinación estacional
function SunLight() {
  const sunPosition = useMemo(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
    // Declinación solar: +23.5° en solsticio de verano, -23.5° en solsticio de invierno
    const declination = 23.5 * Math.sin(2 * Math.PI / 365 * (dayOfYear - 81)) * (Math.PI / 180);
    const sunDistance = 50;
    return [0, sunDistance * Math.sin(declination), sunDistance * Math.cos(declination)] as [number, number, number];
  }, []);
  return <directionalLight position={sunPosition} intensity={params.sunIntensity} color={0xffffff} />;
}

// Componente sin renderizado visual para seguimiento de popup en pantalla + lat/lon
interface PopupTrackerProps {
  selectedSatelliteIndex: number | null;
  satelliteSystemRef: React.RefObject<SatelliteSystemRef>;
  earthSphereRef: React.RefObject<EarthSphereRef>;
  popupRef: React.RefObject<HTMLDivElement | null>;
  positionTextRef: React.RefObject<HTMLSpanElement | null>;
  followingSatelliteIndex: number | null;
  isAnimating: React.MutableRefObject<boolean>;
  earthXOffset: number;
}

// Pre-asignar vectores para evitar presión del recolector de basura en useFrame
const _satWorldPos = new THREE.Vector3();
const _screenPos = new THREE.Vector3();
const _relativePos = new THREE.Vector3();
function PopupTracker(t0) {
  const $ = _c(12);
  const {
    selectedSatelliteIndex,
    satelliteSystemRef,
    earthSphereRef,
    popupRef,
    positionTextRef,
    followingSatelliteIndex,
    isAnimating,
    earthXOffset
  } = t0;
  const {
    camera,
    size
  } = useThree();
  const formatter = useFormatter();
  let t1;
  if ($[0] !== camera || $[1] !== earthSphereRef || $[2] !== earthXOffset || $[3] !== followingSatelliteIndex || $[4] !== formatter || $[5] !== isAnimating || $[6] !== popupRef || $[7] !== positionTextRef || $[8] !== satelliteSystemRef || $[9] !== selectedSatelliteIndex || $[10] !== size) {
    t1 = () => {
      if (selectedSatelliteIndex === null || !popupRef.current) {
        return;
      }
      const satMesh = satelliteSystemRef.current?.getSatelliteMesh(selectedSatelliteIndex);
      if (!satMesh) {
        return;
      }
      satMesh.getWorldPosition(_satWorldPos);
      let x;
      let y;
      const isFollowing = followingSatelliteIndex !== null && !isAnimating.current;
      if (isFollowing) {
        x = size.width / 2;
        y = size.height / 2;
      } else {
        _screenPos.copy(_satWorldPos).project(camera);
        x = (_screenPos.x + 1) / 2 * size.width;
        y = (-_screenPos.y + 1) / 2 * size.height;
      }
      popupRef.current.style.transform = `translate(${x}px, ${y}px)`;
      _relativePos.set(_satWorldPos.x - earthXOffset, _satWorldPos.y, _satWorldPos.z);
      const distance = _relativePos.length();
      const latitude = Math.asin(_relativePos.y / distance) * (180 / Math.PI);
      const worldLonAngle = Math.atan2(_relativePos.z, _relativePos.x);
      const earthRotation = earthSphereRef.current?.getEarthRotation() ?? 0;
      const longitudeRad = -(worldLonAngle + earthRotation);
      let longitude = longitudeRad * 180 / Math.PI;
      while (longitude > 180) {
        longitude = longitude - 360;
      }
      while (longitude < -180) {
        longitude = longitude + 360;
      }
      if (positionTextRef.current) {
        positionTextRef.current.textContent = `${formatter.number(latitude, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        })}°, ${formatter.number(longitude, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        })}°`;
      }
    };
    $[0] = camera;
    $[1] = earthSphereRef;
    $[2] = earthXOffset;
    $[3] = followingSatelliteIndex;
    $[4] = formatter;
    $[5] = isAnimating;
    $[6] = popupRef;
    $[7] = positionTextRef;
    $[8] = satelliteSystemRef;
    $[9] = selectedSatelliteIndex;
    $[10] = size;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  useFrame(t1);
  return null;
}

// Contenido de la escena - Tierra y sus componentes
interface EarthSceneProps {
  satellites?: SatelliteData[];
  satelliteSystemRef: React.RefObject<SatelliteSystemRef>;
  earthSphereRef: React.RefObject<EarthSphereRef>;
  cameraAnimatorRef: React.RefObject<CameraAnimatorRef>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>;
  followingSatelliteIndex: number | null;
  isAnimating: React.RefObject<boolean>;
  selectedSatelliteIndex: number | null;
  popupRef: React.RefObject<HTMLDivElement | null>;
  positionTextRef: React.RefObject<HTMLSpanElement | null>;
  onLoaded: () => void;
  earthXOffset: number;
}
function EarthScene(t0) {
  const $ = _c(45);
  const {
    satellites,
    satelliteSystemRef,
    earthSphereRef,
    cameraAnimatorRef,
    controlsRef,
    followingSatelliteIndex,
    isAnimating,
    selectedSatelliteIndex,
    popupRef,
    positionTextRef,
    onLoaded,
    earthXOffset
  } = t0;
  const [texturesData, setTexturesData] = useState(null);
  let t1;
  if ($[0] !== onLoaded) {
    t1 = (cloudsMap, initialRotation) => {
      setTexturesData({
        cloudsMap,
        initialRotation
      });
      onLoaded();
    };
    $[0] = onLoaded;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleTexturesLoaded = t1;
  const cloudRotationSpeed = EARTH_ROTATION_SPEED * 1.1 * params.speedFactor;
  const t2 = followingSatelliteIndex === null && !isAnimating.current;
  let t3;
  if ($[2] !== earthXOffset) {
    t3 = [earthXOffset, 0, 0];
    $[2] = earthXOffset;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== controlsRef || $[5] !== t2 || $[6] !== t3) {
    t4 = <CameraControls ref={controlsRef} enabled={t2} target={t3} />;
    $[4] = controlsRef;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== satelliteSystemRef) {
    t5 = i => satelliteSystemRef.current?.getSatelliteMesh(i) ?? null;
    $[8] = satelliteSystemRef;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== cameraAnimatorRef || $[11] !== controlsRef || $[12] !== earthXOffset || $[13] !== followingSatelliteIndex || $[14] !== isAnimating || $[15] !== t5) {
    t6 = <CameraAnimator ref={cameraAnimatorRef} followingSatelliteIndex={followingSatelliteIndex} isAnimating={isAnimating} getSatelliteMesh={t5} earthXOffset={earthXOffset} controlsRef={controlsRef} />;
    $[10] = cameraAnimatorRef;
    $[11] = controlsRef;
    $[12] = earthXOffset;
    $[13] = followingSatelliteIndex;
    $[14] = isAnimating;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== earthXOffset) {
    t7 = [earthXOffset, 0, 0];
    $[17] = earthXOffset;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== earthSphereRef || $[20] !== handleTexturesLoaded) {
    t8 = <EarthSphere ref={earthSphereRef} rotationSpeed={EARTH_ROTATION_SPEED} speedFactor={params.speedFactor} onTexturesLoaded={handleTexturesLoaded} />;
    $[19] = earthSphereRef;
    $[20] = handleTexturesLoaded;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== satelliteSystemRef || $[23] !== satellites || $[24] !== texturesData) {
    t9 = texturesData && <><EarthClouds cloudsMap={texturesData.cloudsMap} initialRotation={texturesData.initialRotation} rotationSpeed={cloudRotationSpeed} /><SatelliteSystem ref={satelliteSystemRef} satelliteData={satellites} initialRotation={texturesData.initialRotation} speedFactor={params.speedFactor} /></>;
    $[22] = satelliteSystemRef;
    $[23] = satellites;
    $[24] = texturesData;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <EarthAtmosphere />;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t11 = <group position={t7}>{t8}{t9}{t10}</group>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== earthSphereRef || $[32] !== earthXOffset || $[33] !== followingSatelliteIndex || $[34] !== isAnimating || $[35] !== popupRef || $[36] !== positionTextRef || $[37] !== satelliteSystemRef || $[38] !== selectedSatelliteIndex) {
    t12 = <PopupTracker selectedSatelliteIndex={selectedSatelliteIndex} satelliteSystemRef={satelliteSystemRef} earthSphereRef={earthSphereRef} popupRef={popupRef} positionTextRef={positionTextRef} followingSatelliteIndex={followingSatelliteIndex} isAnimating={isAnimating} earthXOffset={earthXOffset} />;
    $[31] = earthSphereRef;
    $[32] = earthXOffset;
    $[33] = followingSatelliteIndex;
    $[34] = isAnimating;
    $[35] = popupRef;
    $[36] = positionTextRef;
    $[37] = satelliteSystemRef;
    $[38] = selectedSatelliteIndex;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t4 || $[43] !== t6) {
    t13 = <>{t4}{t6}{t11}{t12}</>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t4;
    $[43] = t6;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
}
export function EarthGlobe(t0) {
  const $ = _c(70);
  const {
    satellites,
    style,
    showStats: t1,
    selectedSatelliteIndex: t2,
    earthXOffset: t3,
    legendContent
  } = t0;
  const showStats = t1 === undefined ? false : t1;
  const selectedSatelliteIndex = t2 === undefined ? null : t2;
  const earthXOffset = t3 === undefined ? EARTH_X_OFFSET : t3;
  const satelliteSystemRef = useRef(null);
  const earthSphereRef = useRef(null);
  const cameraAnimatorRef = useRef(null);
  const controlsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTextualAlternativeOpen, setIsTextualAlternativeOpen] = useState(false);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [livePositions, setLivePositions] = useState(t4);
  const popupRef = useRef(null);
  const positionTextRef = useRef(null);
  const t = useTranslations("EarthGlobe");
  const isAnimatingRef = useRef(false);
  const previousSelectedSatelliteIndexRef = useRef(null);
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsLoading(false);
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const handleLoaded = t5;
  let t6;
  let t7;
  if ($[2] !== selectedSatelliteIndex) {
    t6 = () => {
      const previousSelectedSatelliteIndex = previousSelectedSatelliteIndexRef.current;
      previousSelectedSatelliteIndexRef.current = selectedSatelliteIndex;
      if (selectedSatelliteIndex !== null) {
        cameraAnimatorRef.current?.animateCameraToSatellite(selectedSatelliteIndex);
        return;
      }
      if (previousSelectedSatelliteIndex !== null) {
        cameraAnimatorRef.current?.resetCamera();
        return;
      }
      if (controlsRef.current) {
        controlsRef.current.enabled = true;
        controlsRef.current.enableDamping = true;
        controlsRef.current.update();
      }
    };
    t7 = [selectedSatelliteIndex];
    $[2] = selectedSatelliteIndex;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  const selectedSatellite = selectedSatelliteIndex !== null && satellites ? satellites[selectedSatelliteIndex] : null;
  const formatter = useFormatter();
  let t8;
  if ($[5] !== style) {
    t8 = style ?? {};
    $[5] = style;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let canvasStyle;
  let t9;
  if ($[7] !== t8) {
    ({
      height: t9,
      ...canvasStyle
    } = t8);
    $[7] = t8;
    $[8] = canvasStyle;
    $[9] = t9;
  } else {
    canvasStyle = $[8];
    t9 = $[9];
  }
  const height = t9 === undefined ? "100%" : t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsTextualAlternativeOpen(true);
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const handleOpenTextualAlternative = t10;
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setIsTextualAlternativeOpen(false);
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const handleCloseTextualAlternative = t11;
  let t12;
  let t13;
  if ($[12] !== earthXOffset || $[13] !== isTextualAlternativeOpen) {
    t12 = () => {
      if (!isTextualAlternativeOpen) {
        return;
      }
      const update = () => setLivePositions(computeSatellitePositions(satelliteSystemRef, earthSphereRef, earthXOffset));
      update();
      const id = setInterval(update, 1000);
      return () => clearInterval(id);
    };
    t13 = [isTextualAlternativeOpen, earthXOffset];
    $[12] = earthXOffset;
    $[13] = isTextualAlternativeOpen;
    $[14] = t12;
    $[15] = t13;
  } else {
    t12 = $[14];
    t13 = $[15];
  }
  useEffect(t12, t13);
  let t14;
  if ($[16] !== height) {
    t14 = {
      position: "relative",
      width: "100%",
      height
    };
    $[16] = height;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t) {
    t15 = t("canvasLabel");
    $[18] = t;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t) {
    t16 = t("canvasDescription");
    $[20] = t;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t16) {
    t17 = <p id="earth-globe-canvas-description" className="sr-only">{t16}</p>;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== isLoading) {
    t18 = isLoading && <div style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "1rem",
      zIndex: 2
    }}><div style={{
        width: "48px",
        height: "48px",
        border: "4px solid rgba(0, 255, 255, 0.2)",
        borderTop: "4px solid #00ffff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }} /><style>{"\n              @keyframes spin {\n                0% { transform: rotate(0deg); }\n                100% { transform: rotate(360deg); }\n              }\n            "}</style></div>;
    $[24] = isLoading;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== formatter || $[27] !== selectedSatellite || $[28] !== t) {
    t19 = selectedSatellite && <div ref={popupRef} style={{
      position: "absolute",
      left: 0,
      top: 0,
      willChange: "transform",
      transition: "transform 0.05s linear",
      zIndex: 1,
      pointerEvents: "none"
    }}><div style={{
        transform: "translate(-50%, -100%) translateY(-20px)",
        background: "var(--bg)",
        border: "1.2px solid var(--primary)",
        borderRadius: "12px",
        padding: "8px",
        minWidth: "190px"
      }}><div style={{
          textAlign: "center",
          marginBottom: "0.75rem",
          paddingBottom: "0.25rem",
          borderBottom: "1.2px solid var(--primary)"
        }}><span style={{
            color: "var(--black)",
            fontWeight: "bold",
            fontSize: "1rem"
          }}>{selectedSatellite.name}</span></div><div style={{
          display: "flex",
          flexDirection: "column",
          fontSize: "0.8rem",
          marginTop: -8
        }}><div><span style={{
              color: "var(--gray)"
            }}>{t("altitude")}: </span><span style={{
              color: "var(--black)",
              fontWeight: "bold"
            }}>{formatter.number(selectedSatellite.altitude, {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: 2
              })}{" "}km</span></div><div><span style={{
              color: "var(--gray)"
            }}>{t("speed")}: </span><span style={{
              color: "var(--black)",
              fontWeight: "bold"
            }}>{formatter.number(selectedSatellite.speed, {
                maximumFractionDigits: 2
              })}{" "}km/h</span></div><div><span style={{
              color: "var(--gray)"
            }}>{t("heading")}: </span><span style={{
              color: "var(--black)",
              fontWeight: "bold"
            }}>{formatter.number(selectedSatellite.heading, {
                maximumFractionDigits: 2
              })}°</span></div><div><span style={{
              color: "var(--gray)"
            }}>{t("position")}: </span><span ref={positionTextRef} style={{
              color: "var(--black)",
              fontWeight: "bold"
            }} /></div></div></div></div>;
    $[26] = formatter;
    $[27] = selectedSatellite;
    $[28] = t;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== isLoading || $[31] !== legendContent || $[32] !== satellites?.length || $[33] !== selectedSatelliteIndex) {
    t20 = !isLoading && <GlobeAccessibilityControls controlsRef={controlsRef} cameraAnimatorRef={cameraAnimatorRef} selectedSatelliteIndex={selectedSatelliteIndex} onTextualAlternative={satellites?.length ? handleOpenTextualAlternative : undefined} legendContent={legendContent} />;
    $[30] = isLoading;
    $[31] = legendContent;
    $[32] = satellites?.length;
    $[33] = selectedSatelliteIndex;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== isTextualAlternativeOpen || $[36] !== livePositions || $[37] !== satellites) {
    t21 = satellites?.length && <GlobeTextualAlternativeModal isOpen={isTextualAlternativeOpen} onClose={handleCloseTextualAlternative} satellites={satellites} livePositions={livePositions} />;
    $[35] = isTextualAlternativeOpen;
    $[36] = livePositions;
    $[37] = satellites;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = isLoading ? 0 : 1;
  let t23;
  if ($[39] !== canvasStyle || $[40] !== height || $[41] !== t22) {
    t23 = {
      ...canvasStyle,
      position: "absolute",
      top: 0,
      left: 0,
      height,
      opacity: t22,
      transition: "opacity 0.5s ease-in-out"
    };
    $[39] = canvasStyle;
    $[40] = height;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  const t24 = isLoading ? "demand" : "always";
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      fov: 45,
      near: 1,
      far: 1000,
      position: INITIAL_CAMERA_POSITION
    };
    t26 = {
      antialias: true,
      alpha: true,
      outputColorSpace: THREE.SRGBColorSpace
    };
    t27 = [1, 2];
    t28 = <CameraSetup />;
    t29 = <SunLight />;
    $[43] = t25;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
  } else {
    t25 = $[43];
    t26 = $[44];
    t27 = $[45];
    t28 = $[46];
    t29 = $[47];
  }
  let t30;
  if ($[48] !== earthXOffset || $[49] !== satellites || $[50] !== selectedSatelliteIndex) {
    t30 = <EarthScene satellites={satellites} satelliteSystemRef={satelliteSystemRef} earthSphereRef={earthSphereRef} cameraAnimatorRef={cameraAnimatorRef} controlsRef={controlsRef} followingSatelliteIndex={selectedSatelliteIndex} isAnimating={isAnimatingRef} selectedSatelliteIndex={selectedSatelliteIndex} popupRef={popupRef} positionTextRef={positionTextRef} onLoaded={handleLoaded} earthXOffset={earthXOffset} />;
    $[48] = earthXOffset;
    $[49] = satellites;
    $[50] = selectedSatelliteIndex;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== showStats) {
    t31 = showStats && <Stats />;
    $[52] = showStats;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== t24 || $[55] !== t30 || $[56] !== t31) {
    t32 = <Canvas frameloop={t24} camera={t25} gl={t26} dpr={t27}>{t28}{t29}{t30}{t31}</Canvas>;
    $[54] = t24;
    $[55] = t30;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== t23 || $[59] !== t32) {
    t33 = <div style={t23}>{t32}</div>;
    $[58] = t23;
    $[59] = t32;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t14 || $[62] !== t15 || $[63] !== t17 || $[64] !== t18 || $[65] !== t19 || $[66] !== t20 || $[67] !== t21 || $[68] !== t33) {
    t34 = <div style={t14} role="region" aria-label={t15} aria-describedby="earth-globe-canvas-description">{t17}{t18}{t19}{t20}{t21}{t33}</div>;
    $[61] = t14;
    $[62] = t15;
    $[63] = t17;
    $[64] = t18;
    $[65] = t19;
    $[66] = t20;
    $[67] = t21;
    $[68] = t33;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  return t34;
}
export default EarthGlobe;